import React, { useContext, useEffect, useState } from "react";
import { Modal, Table, Button, Form, Select, InputNumber, message, Tabs, Tag, Popconfirm, Tooltip, Space } from "antd";
import { PlusOutlined, DeleteOutlined, ReloadOutlined, EditOutlined, CheckOutlined } from "@ant-design/icons";
import { GetBotinRecompenses, PostBotinRecompensa, PutBotinRecompensa, DeleteBotinRecompensa, GetBotinUsuarisRecompenses, DeleteBotinRecompensaUsuari } from "../../../dao/jutge/BotinesDao";
import { GetJavamonConfig, GetSkins } from "../../../dao/jutge/GetJavamonConfig";
import { GetShopItems } from "../../../dao/jutge/GetShopItems";
import { AuthContext } from "../../../config/AuthContext";

const { Option } = Select;
const { TabPane } = Tabs;
const genericUrl = process.env.REACT_APP_API_URL;

interface Props {
    botin: any; // { id, identifier, nom, ... }
    visible: boolean;
    isAdmin?: boolean;
    onClose: () => void;
}

/**
 * Editor de recompensas de un botín. Reutiliza los mismos tipos de recompensa que
 * el gestor de recompensas de ejercicios (PremiosModal), incluyendo el nuevo
 * JAVAMON_SEASON (javamon de una temporada concreta).
 */
const BotinRecompensesModal: React.FC<Props> = ({ botin, visible, isAdmin: propIsAdmin, onClose }) => {
    const { user } = useContext(AuthContext);
    const isRoot = propIsAdmin !== undefined ? propIsAdmin : (String(user?.tipus) === "0");

    const [loading, setLoading] = useState(false);
    const [recompenses, setRecompenses] = useState<any[]>([]);
    const [historial, setHistorial] = useState<any[]>([]);
    const [tipoSeleccionado, setTipoSeleccionado] = useState<string>('');
    const [editingId, setEditingId] = useState<string | number | null>(null);

    // Datos de configuración para poblar los selectores de item
    const [javamons, setJavamons] = useState<any[]>([]);
    const [seasons, setSeasons] = useState<any[]>([]);
    const [shopItems, setShopItems] = useState<any[]>([]);
    const [skinsData, setSkinsData] = useState<any>({});

    const [form] = Form.useForm();

    useEffect(() => {
        if (visible && botin) {
            loadRecompenses();
            loadConfigData();
            loadHistorial();
        }
    }, [visible, botin]);

    async function loadRecompenses() {
        setLoading(true);
        const data = await GetBotinRecompenses(botin.id);
        setRecompenses(data || []);
        setLoading(false);
    }

    async function loadHistorial() {
        const data = await GetBotinUsuarisRecompenses(botin.id);
        setHistorial(Array.isArray(data) ? data : []);
    }

    async function loadConfigData() {
        const jConfig = await GetJavamonConfig();
        if (jConfig) {
            const parse = (v: any) => (typeof v === 'string' && v && v !== 'null' ? JSON.parse(v) : (Array.isArray(v) ? v : []));
            setJavamons(jConfig.javamons ? parse(jConfig.javamons) : []);
            setSeasons(jConfig.seasons ? parse(jConfig.seasons) : []);
        }
        const sData = await GetSkins();
        if (sData) setSkinsData(sData);
        const shopData = await GetShopItems("0");
        if (shopData) setShopItems(shopData);
    }

    function handleEditRecompensa(record: any) {
        setEditingId(record.id);
        setTipoSeleccionado(record.tipo);
        form.setFieldsValue({
            tipo: record.tipo,
            valor: record.valor,
            id_item: record.id_item ? (isNaN(Number(record.id_item)) ? record.id_item : Number(record.id_item)) : undefined,
            limit_quantity: record.limit_quantity
        });
    }

    function handleCancelEdit() {
        setEditingId(null);
        setTipoSeleccionado('');
        form.resetFields();
    }

    async function handleAddRecompensa(values: any) {
        if (!isRoot) {
            message.warning("Solo los administradores pueden añadir recompensas al botín.");
            return;
        }
        setLoading(true);
        let ok = false;
        if (editingId) {
            ok = await PutBotinRecompensa(botin.id, editingId, values);
            if (ok) {
                message.success("Recompensa actualizada");
                handleCancelEdit();
                await loadRecompenses();
            } else {
                message.error("Error al actualizar la recompensa");
            }
        } else {
            ok = await PostBotinRecompensa(botin.id, values);
            if (ok) {
                message.success("Recompensa añadida al botín");
                handleCancelEdit();
                await loadRecompenses();
            } else {
                message.error("Error al añadir la recompensa");
            }
        }
        setLoading(false);
    }

    async function handleDeleteRecompensa(id: string) {
        if (!isRoot) {
            message.warning("Solo los administradores pueden eliminar recompensas del botín.");
            return;
        }
        setLoading(true);
        const ok = await DeleteBotinRecompensa(botin.id, id);
        if (ok) {
            message.success("Recompensa eliminada");
            if (editingId === id) handleCancelEdit();
            await loadRecompenses();
        } else {
            message.error("Error al eliminar la recompensa");
        }
        setLoading(false);
    }

    // Tipos que NO requieren seleccionar un item concreto
    const noItemTypes = ["PUNTUACION", "JAVAMON_RANDOM"];
    // Tipos cuyo id_item es un javamon / temporada, no un item de equipamiento
    const isSeasonType = tipoSeleccionado === "JAVAMON_SEASON";
    const isJavamonSpecific = tipoSeleccionado === "JAVAMON";

    function itemListForType(tipo: string): any[] {
        if (tipo === "JAVAMON") return javamons;
        if (tipo === "SHOP_ITEM") return shopItems;
        if (tipo === "SKIN") return skinsData.skins || [];
        if (tipo === "ARMA") return skinsData.armes || [];
        if (tipo === "ESCUDO") return skinsData.escuts || [];
        if (tipo === "ARCO") return skinsData.arcs || [];
        if (tipo === "FLECHA") return skinsData.flechas || [];
        if (tipo === "MAGIA") return skinsData.magias || [];
        if (tipo === "AVATAR") return skinsData.avatars || [];
        return [];
    }

    function imgUrlForItem(tipo: string, item: any): string | null {
        if (tipo === "JAVAMON") return `${genericUrl}:8061/jutge/game/image/javamon/${item.id}`;
        if (tipo === "SHOP_ITEM") {
            if (item.image && item.image.startsWith('data:')) return item.image;
            if (item.image) return `data:image/png;base64,${item.image}`;
            return null;
        }
        if (item.imagen && item.imagen.startsWith('data:')) return item.imagen;
        if (item.imagen) return `data:image/png;base64,${item.imagen}`;
        let seg = tipo.toLowerCase();
        if (seg === "escudo") seg = "escut";
        if (seg === "arco") seg = "arc";
        return `${genericUrl}:8061/jutge/game/image/${seg}/${item.id}`;
    }

    const columns = [
        { title: "Tipo", dataIndex: "tipo", width: 130 },
        { title: "Valor", dataIndex: "valor", width: 90, align: 'center' as const, render: (v: any) => v ? <b style={{ color: '#faad14' }}>{v}</b> : '-' },
        { title: "Límite", dataIndex: "limit_quantity", width: 100, align: 'center' as const, render: (v: any) => v || 'Infinito' },
        { title: "Repartidas", dataIndex: "claimed_count", width: 100, align: 'center' as const, render: (v: any) => v || 0 },
        {
            title: "Item",
            key: "item",
            ellipsis: true,
            render: (r: any) => {
                if (!r.id_item || noItemTypes.includes(r.tipo)) return '-';
                if (r.tipo === "JAVAMON_SEASON") {
                    const s = seasons.find((x: any) => x.id + "" === r.id_item + "");
                    return s ? `Temporada: ${s.nom}` : `Temporada #${r.id_item}`;
                }
                const list = itemListForType(r.tipo);
                const item = list.find((x: any) => x.id + "" === r.id_item + "");
                const name = item ? (item.nom || item.name) : `ID: ${r.id_item}`;
                const url = item ? imgUrlForItem(r.tipo, item) : null;
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {url && <img src={url} alt="" style={{ width: 24, height: 24, marginRight: 8, objectFit: 'contain' }} />}
                        {name}
                    </div>
                );
            }
        },
        ...(isRoot ? [{
            title: "Acciones",
            key: "acciones",
            width: 110,
            align: 'center' as const,
            render: (r: any) => (
                <Space size={4}>
                    <Tooltip title="Editar recompensa">
                        <Button type="text" icon={<EditOutlined />} onClick={() => handleEditRecompensa(r)} />
                    </Tooltip>
                    <Popconfirm
                        title="¿Eliminar esta recompensa?"
                        onConfirm={() => handleDeleteRecompensa(r.id)}
                        okText="Eliminar"
                        cancelText="Cancelar"
                    >
                        <Button type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        }] : [])
    ];

    async function handleDeleteReclamo(idReclamo: string | number) {
        if (!isRoot) {
            message.error("Solo los usuarios root (rol 0) pueden eliminar reclamos.");
            return;
        }
        setLoading(true);
        const ok = await DeleteBotinRecompensaUsuari(botin.id, idReclamo);
        if (ok) {
            message.success("Reclamo eliminado. El usuario podrá volver a reclamar la recompensa si cumple los requisitos.");
            await loadHistorial();
            await loadRecompenses(); // Refresca claimed_count en la tabla de recompensas
        } else {
            message.error("Error al eliminar el reclamo");
        }
        setLoading(false);
    }

    const [historialSearch, setHistorialSearch] = useState<string>('');

    const historialFiltered = historial.filter((r: any) => {
        if (!historialSearch) return true;
        const query = historialSearch.toLowerCase();
        const fullName = `${r.nom || ''} ${r.cognoms || ''}`.toLowerCase();
        const email = (r.email || '').toLowerCase();
        const idUser = `${r.id_usuari || ''}`;
        const aula = (r.aula || '').toLowerCase();
        return fullName.includes(query) || email.includes(query) || idUser.includes(query) || aula.includes(query);
    });

    const historialColumns = [
        {
            title: "Alumno",
            key: "alumno",
            render: (r: any) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {r.avatar && <img src={r.avatar} alt="" style={{ width: 28, height: 28, borderRadius: '50%', marginRight: 8, objectFit: 'cover' }} />}
                    <div>
                        <div style={{ fontWeight: 500 }}>{`${r.nom || ''} ${r.cognoms || ''}`.trim() || `#${r.id_usuari}`}</div>
                        {r.email && <div style={{ fontSize: 11, color: '#888' }}>{r.email}</div>}
                    </div>
                </div>
            )
        },
        { title: "Aula", key: "aula", render: (r: any) => r.aula ? <Tag color="blue">{r.aula}</Tag> : (r.id_aula ? `#${r.id_aula}` : '-') },
        {
            title: "Recompensa",
            key: "recompensa",
            render: (r: any) => {
                if (r.tipo === 'PUNTUACION') return <Tag color="gold">{r.valor} punts</Tag>;
                if (r.tipo === 'JAVAMON' || r.tipo === 'JAVAMON_RANDOM' || r.tipo === 'JAVAMON_SEASON') {
                    return <Tag color="purple">Javamon{r.id_javamon ? ` #${r.id_javamon}` : ''}</Tag>;
                }
                return <Tag>{r.tipo}{r.id_item ? ` #${r.id_item}` : ''}</Tag>;
            }
        },
        {
            title: "Estado",
            key: "estado",
            render: (r: any) => {
                if (!r.estado_javamon) return '-';
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div>
                            {r.estado_javamon === 'CAPTURADO'
                                ? <Tag color="green">Capturado</Tag>
                                : <Tag color="red">Escapado</Tag>}
                        </div>
                        {r.javaball_lanzada ? (
                            <span style={{ fontSize: 11, color: '#595959' }}>
                                🎯 <b>{r.javaball_lanzada}</b>
                                {r.javaball_origen ? ` (${r.javaball_origen === 'GAME' ? 'Juego' : 'Web'})` : ''}
                            </span>
                        ) : (
                            <span style={{ fontSize: 11, color: '#8c8c8c' }}>Sin lanzar Javaball</span>
                        )}
                    </div>
                );
            }
        },
        {
            title: "Fecha",
            dataIndex: "created_at",
            key: "created_at",
            render: (v: any) => v ? new Date(v).toLocaleString() : '-'
        },
        ...(isRoot ? [{
            title: "Acciones",
            key: "acciones",
            align: "center" as const,
            render: (r: any) => (
                <Popconfirm
                    title="¿Eliminar este reclamo?"
                    description="El usuario podrá volver a reclamar esta recompensa si cumple los requisitos."
                    onConfirm={() => handleDeleteReclamo(r.id)}
                    okText="Eliminar"
                    cancelText="Cancelar"
                >
                    <Tooltip title="Eliminar reclamo (permitir volver a reclamar)">
                        <Button type="text" danger icon={<DeleteOutlined />} />
                    </Tooltip>
                </Popconfirm>
            )
        }] : [])
    ];

    return (
        <Modal
            title={`Recompensas del botín: ${botin?.nom || ''} (${botin?.identifier || ''})`}
            open={visible}
            onCancel={onClose}
            footer={null}
            width={900}
            destroyOnClose
        >
          <Tabs defaultActiveKey="recompenses">
            <TabPane tab="Recompensas" key="recompenses">
            {isRoot && (
                <>
                    {editingId && (
                        <div style={{
                            marginBottom: 14, padding: '10px 16px', background: '#f0f7ff',
                            border: '1px solid #adc6ff', borderRadius: 8, display: 'flex',
                            justifyContent: 'space-between', alignItems: 'center',
                            boxShadow: '0 2px 6px rgba(24,144,255,0.08)'
                        }}>
                            <span style={{ color: '#0958d9', fontWeight: 500 }}>
                                ✏️ <b>Modificando recompensa #{editingId}</b> — Haz los cambios y pulsa "Guardar".
                            </span>
                            <Button size="small" type="link" onClick={handleCancelEdit} style={{ color: '#ff4d4f' }}>
                                Cancelar edición
                            </Button>
                        </div>
                    )}
                    <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: '14px 16px 6px 16px', marginBottom: 16 }}>
                        <Form form={form} layout="inline" onFinish={handleAddRecompensa} style={{ rowGap: 12 }}>
                            <Form.Item name="tipo" rules={[{ required: true, message: 'Tipo necesario' }]}>
                                <Select placeholder="Tipo" style={{ width: 190 }} onChange={(v) => { setTipoSeleccionado(v); form.setFieldsValue({ id_item: undefined }); }}>
                                    <Option value="PUNTUACION">Puntuación</Option>
                                    <Option value="JAVAMON_RANDOM">Javamon (Aleatorio)</Option>
                                    <Option value="JAVAMON">Javamon Específico</Option>
                                    <Option value="JAVAMON_SEASON">Javamon de Temporada</Option>
                                    <Option value="SHOP_ITEM">Objeto de Tienda</Option>
                                    <Option value="SKIN">Skin</Option>
                                    <Option value="ARMA">Arma</Option>
                                    <Option value="ESCUDO">Escudo</Option>
                                    <Option value="ARCO">Arco</Option>
                                    <Option value="FLECHA">Flecha</Option>
                                    <Option value="MAGIA">Magia</Option>
                                    <Option value="AVATAR">Avatar</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item name="valor" tooltip="Cantidad de puntos (solo Puntuación)">
                                <InputNumber placeholder="Valor" disabled={tipoSeleccionado !== 'PUNTUACION'} />
                            </Form.Item>

                            {isSeasonType ? (
                                <Form.Item name="id_item" rules={[{ required: true, message: 'Temporada necesaria' }]} tooltip="Temporada de la que saldrá el javamon">
                                    <Select placeholder="Selecciona Temporada" style={{ width: 220 }} showSearch optionFilterProp="children">
                                        {seasons.map((s: any) => (
                                            <Option key={s.id} value={s.id} label={s.nom}>{s.nom}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            ) : (tipoSeleccionado && !noItemTypes.includes(tipoSeleccionado)) ? (
                                <Form.Item name="id_item" rules={[{ required: true, message: 'Item necesario' }]} tooltip="Selecciona el item">
                                    <Select placeholder="Selecciona Item" style={{ width: 250 }} showSearch optionFilterProp="children">
                                        {itemListForType(tipoSeleccionado).map((item: any) => {
                                            const url = imgUrlForItem(tipoSeleccionado, item);
                                            return (
                                                <Option key={item.id} value={item.id} label={item.nom || item.name}>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        {url && <img src={url} alt="" style={{ width: 20, height: 20, marginRight: 8, objectFit: 'contain' }} />}
                                                        {item.nom || item.name || `Item ${item.id}`}
                                                    </div>
                                                </Option>
                                            );
                                        })}
                                    </Select>
                                </Form.Item>
                            ) : (
                                <Form.Item name="id_item" hidden><InputNumber /></Form.Item>
                            )}

                            <Form.Item name="limit_quantity" tooltip="Máximo de unidades repartibles entre todos los usuarios (vacío = infinito)">
                                <InputNumber placeholder="Límite Unidades" min={1} />
                            </Form.Item>

                            <Form.Item>
                                <Space>
                                    <Button type="primary" htmlType="submit" icon={editingId ? <CheckOutlined /> : <PlusOutlined />} loading={loading}>
                                        {editingId ? "Guardar" : "Añadir"}
                                    </Button>
                                    {editingId && (
                                        <Button onClick={handleCancelEdit}>Cancelar</Button>
                                    )}
                                </Space>
                            </Form.Item>
                        </Form>
                    </div>
                </>
            )}

            <Table rowKey="id" dataSource={recompenses} columns={columns} loading={loading} pagination={false} />
            </TabPane>

            <TabPane tab={`Historial de reclamos${historial.length ? ` (${historial.length})` : ''}`} key="historial">
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, alignItems: 'center' }}>
                    <input
                        type="text"
                        placeholder="Buscar por alumno o aula..."
                        value={historialSearch}
                        onChange={(e) => setHistorialSearch(e.target.value)}
                        style={{
                            padding: '4px 11px',
                            border: '1px solid #d9d9d9',
                            borderRadius: 4,
                            width: 260
                        }}
                    />
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={async () => {
                            setLoading(true);
                            await loadHistorial();
                            setLoading(false);
                        }}
                    >
                        Recargar
                    </Button>
                </div>
                <Table
                    rowKey="id"
                    dataSource={historialFiltered}
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    columns={historialColumns}
                />
            </TabPane>
          </Tabs>
        </Modal>
    );
};

export default BotinRecompensesModal;
